import { useCallback, useMemo } from 'react';
import type { RxDocument } from 'rxdb';
import { useSetAtom } from 'jotai';

import {
  useRxDocument,
  useRxWriter,
  useRxPatchMutation,
  useRXDeleteByIds,
  useRxCreateMutation,
  useDuplicateMutation,
  useRxDocumentLazy,
  useRxDocuments,
  useRxDocumentsLazy,
  type MutationOptions,
  type RxQueryParams,
} from '@/hooks/rxdb-base';
import { useSearch, convertSortToString, type UseWithSearchParams } from '@/hooks/use-search';
import { useConditionalSearch, type ConditionalSearchResult } from '@/hooks/use-conditional-search';

import type { Note, UserSource, UserSourceAvailabilityScope } from '@flintmint/core-shared/rxdb';
import { processingUploadIdsAtom } from '@/state';
import { createBlankEditorContent } from './use-notes';

// create

export const useUserSourceCreate = () => {
  const { mutate: createMutation, isLoading } = useRxCreateMutation<UserSource>('userSources');
  const setProcessingUploadIds = useSetAtom(processingUploadIdsAtom);

  const createWithDefaults = async (payload: Partial<UserSource>, mutationOptions?: MutationOptions) => {
    const userSourceData = {
      content: createBlankEditorContent(),
      name: '',
      sourceType: '',
      source: null,
      syncStatus: 'ready' as const,
      availabilityScope: 'global' as const,
      tagIds: [],
      ...payload,
    };

    const created = await createMutation(userSourceData, mutationOptions);
    // Lets the app-level refusal watcher report this upload if its processing fails.
    if (userSourceData.syncStatus === 'processing') setProcessingUploadIds((ids) => [...ids, created.id]);
    return created;
  };

  return { mutate: createWithDefaults, isLoading };
};

export const useUserSourceDuplicate = () => useDuplicateMutation<UserSource>('userSources', { availabilityScope: 'global' });

// read

export const useUserSource = (id?: string) => useRxDocument<UserSource>('userSources', id);

export const useUserSourceLazy = () => useRxDocumentLazy<UserSource>('userSources');

export const useUserSources = (queryParams?: RxQueryParams<UserSource>) => useRxDocuments<UserSource>('userSources', queryParams);

/** The live sources with these ids; queries nothing while `ids` is empty. */
export const useUserSourcesByIds = (ids: string[]) =>
  useRxDocuments<UserSource>('userSources', { ids }, { enabled: ids.length > 0 });

export const useResolvedUserSourceIds = (ids: string[]) => {
  // Ref-map keys on notes/blocks (e.g. note.userSources) are soft references: cleanup is
  // best-effort GC (see getPostRemove in rxdb-middleware.ts), so a key can outlive its doc.
  // Only ids that still resolve to a live doc count as attached. While the query is still
  // resolving (e.g. first mount), fall back to the raw ids so a note that actually has sources
  // doesn't briefly read as empty.
  const { data: resolvedUserSources, isLoading } = useUserSources({ ids });

  return useMemo(() => (isLoading ? ids : resolvedUserSources.map((source) => source.id)), [isLoading, ids, resolvedUserSources]);
};

export const useEnabledUserSources = (note?: Note) => {
  const enabledUserSourceIds = getEnabledUserSourceIds(note);
  return useRxDocuments<UserSource>('userSources', { ids: enabledUserSourceIds });
};

export const useEnabledUserSourcesLazy = () => {
  const { query: queryDocuments, isLoading } = useRxDocumentsLazy<UserSource>('userSources');

  const query = async (note?: Note) => {
    const enabledUserSourceIds = getEnabledUserSourceIds(note);
    if (enabledUserSourceIds.length === 0) {
      return [];
    }
    return queryDocuments({ ids: enabledUserSourceIds });
  };

  return { query, isLoading };
};

const getEnabledUserSourceIds = (note?: Note) => {
  return Object.entries(note?.userSources || {}).flatMap(([id, value]) => (value.enabled ? [id] : []));
};

export const useUserSourcesWithSearch = ({
  searchTerm,
  ids: userSourceIds,
  sort,
  tagIds,
  availabilityScopes,
  highlightMatches = false,
}: UseWithSearchParams<UserSource> & {
  availabilityScopes?: UserSourceAvailabilityScope[];
}): ConditionalSearchResult<UserSource> => {
  const isSearching = !!searchTerm;

  const searchQuery = {
    term: searchTerm,
    where: {
      ...(userSourceIds && { ids: userSourceIds }),
      types: ['userSources'],
      tagIds,
      availabilityScopes,
    },
    sort: convertSortToString(sort),
    highlightMatches,
  };
  const searchResults = useSearch(searchQuery, isSearching);

  const rxdbResults = useUserSources({
    ids: userSourceIds,
    sort,
    tagIds,
    availabilityScopes,
  });

  return useConditionalSearch<UserSource>(searchResults, rxdbResults, isSearching);
};

export const useUserSourceMakeGlobal = () => {
  const { query: queryUserSource, isLoading: isQuerying } = useUserSourceLazy();
  const { mutate: patchUserSource, isLoading: isPatching } = useUserSourcePatch();

  const makeGlobal = useCallback(
    async (userSourceId: string) => {
      const userSource = await queryUserSource(userSourceId);
      if (!userSource) {
        return;
      }
      return patchUserSource(userSource, { availabilityScope: 'global' });
    },
    [queryUserSource, patchUserSource]
  );

  return { mutate: makeGlobal, isLoading: isQuerying || isPatching };
};

// update

export const useUserSourcePatch = () => useRxPatchMutation<UserSource>();

export const useUserSourceWriter = (
  userSourceRxDoc: RxDocument<UserSource> | undefined,
  userSourceId: string | undefined,
  { createIfMissing }: { createIfMissing: boolean }
) =>
  useRxWriter(userSourceRxDoc, userSourceId, {
    collectionName: 'userSources',
    create: useUserSourceCreate(),
    patch: useUserSourcePatch(),
    createIfMissing,
  });

// delete
export const useUserSourceDeleteByIds = () => {
  const { mutate: deleteUserSources, isLoading } = useRXDeleteByIds<UserSource>('userSources');

  const deleteUserSourcesAndCloseTheirTabs = async (ids: string[], mutationOptions: MutationOptions = {}) => {
    if (!ids.length) return;

    await deleteUserSources(ids, {
      ...mutationOptions,
      onSuccess: (result) => {
        mutationOptions.onSuccess?.(result);
      },
    });
  };

  return { mutate: deleteUserSourcesAndCloseTheirTabs, isLoading };
};
